import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { CheckCircle2, AlertCircle } from 'lucide-react';
import { createAgent } from '@/lib/api';
import { useNavigate } from 'react-router-dom';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';

const createAgentSchema = z.object({
  name: z.string()
    .min(3, '名稱至少 3 個字元')
    .max(50, '名稱最多 50 個字元')
    .regex(/^[a-z0-9-]+$/, '只能使用小寫字母、數字和連字號'),
  displayName: z.string()
    .min(1, '顯示名稱為必填')
    .max(100, '顯示名稱最多 100 個字元'),
  description: z.string().max(500, '描述最多 500 字元').optional(),
  tags: z.string().optional(),
  userRegistrationMode: z.enum(['auto', 'whitelist']),
  status: z.enum(['draft', 'running']),
});

type CreateAgentForm = z.infer<typeof createAgentSchema>;

interface CreateAgentFormProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
}

export function CreateAgentForm({ open, onOpenChange, onSuccess }: CreateAgentFormProps) {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [step, setStep] = useState<1 | 2>(1);
  const [agentId, setAgentId] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
    setValue,
  } = useForm<CreateAgentForm>({
    resolver: zodResolver(createAgentSchema),
    defaultValues: {
      name: '',
      displayName: '',
      description: '',
      tags: '',
      userRegistrationMode: 'auto',
      status: 'draft',
    },
  });

  const nameValue = watch('name');
  const tagsValue = watch('tags', '');

  const onSubmit = async (data: CreateAgentForm) => {
    if (step === 1) {
      // Validate and move to step 2
      setStep(2);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const result = await createAgent({
        ...data,
        tags: data.tags ? data.tags.split(',').map(t => t.trim()).filter(Boolean) : [],
      });

      setAgentId(result.agentId);

      // Show success state
      setTimeout(() => {
        onOpenChange(false);
        onSuccess?.();
        // Reset form
        setStep(1);
        setAgentId(null);
        navigate(`/agent/${result.agentId}`);
      }, 2000);
    } catch (err) {
      setError(err instanceof Error ? err.message : '建立失敗');
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    if (!isLoading) {
      onOpenChange(false);
      setStep(1);
      setError(null);
      setAgentId(null);
    }
  };

  if (agentId) {
    return (
      <Dialog open={open} onOpenChange={handleClose}>
        <DialogContent className="sm:max-w-md">
          <div className="flex flex-col items-center py-8 animate-scale-in">
            <div className="w-16 h-16 rounded-full bg-status-running/10 flex items-center justify-center mb-4 animate-float">
              <CheckCircle2 className="h-8 w-8 text-status-running" />
            </div>
            <DialogTitle className="text-center mb-2">Agent 建立成功！</DialogTitle>
            <DialogDescription className="text-center">
              agentId: <code className="text-xs bg-muted px-2 py-1 rounded">{agentId}</code>
            </DialogDescription>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle>建立新 Agent</DialogTitle>
          <DialogDescription>
            {step === 1
              ? '填寫 Agent 基本資訊'
              : '確認配置並建立 Agent'}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          {error && (
            <div className="flex items-center gap-2 p-3 rounded-lg bg-destructive/10 text-destructive text-sm animate-fade-in">
              <AlertCircle className="h-4 w-4 flex-shrink-0" />
              <span>{error}</span>
            </div>
          )}

          {step === 1 ? (
            <>
              {/* Step 1: Basic Info */}
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="name">
                    Agent 名稱 <span className="text-destructive">*</span>
                  </Label>
                  <Input
                    id="name"
                    placeholder="customer-service-v1"
                    {...register('name')}
                    className={cn(errors.name && 'border-destructive')}
                  />
                  {errors.name && (
                    <p className="text-xs text-destructive">{errors.name.message}</p>
                  )}
                  <p className="text-xs text-muted-foreground">
                    只能使用小寫字母、數字和連字號，3-50 字元
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="displayName">
                    顯示名稱 <span className="text-destructive">*</span>
                  </Label>
                  <Input
                    id="displayName"
                    placeholder="客戶服務機器人"
                    {...register('displayName')}
                    className={cn(errors.displayName && 'border-destructive')}
                  />
                  {errors.displayName && (
                    <p className="text-xs text-destructive">{errors.displayName.message}</p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="description">描述</Label>
                  <Textarea
                    id="description"
                    placeholder="說明此 Agent 的用途..."
                    rows={3}
                    {...register('description')}
                    maxLength={500}
                  />
                  <p className="text-xs text-muted-foreground">
                    最多 500 字元（選填）
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="tags">標籤</Label>
                  <Input
                    id="tags"
                    placeholder="客服, LINE, 新產品（用逗號分隔）"
                    {...register('tags')}
                  />
                  {tagsValue && (
                    <div className="flex flex-wrap gap-2 mt-2">
                      {tagsValue.split(',').map((tag, i) => (
                        tag.trim() && (
                          <Badge key={i} variant="secondary" className="text-xs">
                            {tag.trim()}
                          </Badge>
                        )
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </>
          ) : (
            <>
              {/* Step 2: Confirm */}
              <div className="space-y-4 animate-fade-in">
                <div className="bg-muted/50 rounded-lg p-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">Agent 名稱</span>
                    <span className="font-mono text-sm">{nameValue}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">顯示名稱</span>
                    <span className="text-sm">{watch('displayName')}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">用戶註冊模式</span>
                    <Badge variant="secondary">
                      {watch('userRegistrationMode') === 'auto' ? '自動' : '白名單'}
                    </Badge>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">初始狀態</span>
                    <Badge
                      variant={watch('status') === 'running' ? 'status-running' : 'status-draft'}
                    >
                      {watch('status') === 'running' ? '運行中' : '草稿'}
                    </Badge>
                  </div>
                </div>

                <div className="space-y-3">
                  <Label>用戶註冊模式</Label>
                  <Select
                    value={watch('userRegistrationMode')}
                    onValueChange={(value) => setValue('userRegistrationMode', value as 'auto' | 'whitelist')}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="auto">
                        <div className="flex flex-col gap-1">
                          <span className="font-medium">自動模式</span>
                          <span className="text-xs text-muted-foreground">
                            任何用戶都可以直接與 Agent 互動
                          </span>
                        </div>
                      </SelectItem>
                      <SelectItem value="whitelist">
                        <div className="flex flex-col gap-1">
                          <span className="font-medium">白名單模式</span>
                          <span className="text-xs text-muted-foreground">
                            只有被添加到白名單的用戶可以互動
                          </span>
                        </div>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-3">
                  <Label>初始狀態</Label>
                  <Select
                    value={watch('status')}
                    onValueChange={(value) => setValue('status', value as 'draft' | 'running')}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="draft">
                        <div className="flex items-center gap-2">
                          <div className="w-2 h-2 rounded-full bg-status-draft" />
                          <span>草稿</span>
                        </div>
                      </SelectItem>
                      <SelectItem value="running">
                        <div className="flex items-center gap-2">
                          <div className="w-2 h-2 rounded-full bg-status-running animate-pulse" />
                          <span>運行中</span>
                        </div>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </>
          )}

          <DialogFooter>
            {step === 1 ? (
              <>
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleClose}
                  disabled={isLoading}
                >
                  取消
                </Button>
                <Button type="submit" disabled={isLoading}>
                  下一步
                </Button>
              </>
            ) : (
              <>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setStep(1)}
                  disabled={isLoading}
                >
                  上一步
                </Button>
                <Button
                  type="submit"
                  disabled={isLoading}
                  className="shadow-lg shadow-primary/25"
                >
                  {isLoading ? '建立中...' : '建立 Agent'}
                </Button>
              </>
            )}
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
